<template>
  <div class="contribution-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">投稿审核</h3>
        </div>
      </div>

      <!-- 状态筛选标签 -->
      <div class="flex items-center gap-2">
        <button
          v-for="tab in filterTabs"
          :key="tab.value"
          @click="statusFilter = tab.value"
          :class="[
            'px-4 py-1.5 text-sm rounded-full border transition-all',
            statusFilter === tab.value
              ? 'bg-[#F2C94C] text-white border-[#F2C94C] font-medium'
              : 'bg-white text-slate-600 border-slate-200 hover:border-[#F2C94C] hover:text-[#F2C94C]'
          ]"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">用户在前台「投稿」功能提交的素材会进入此列表，审核通过后自动发布到对应内容模块</p>
      </div>
    </div>

    <!-- 投稿表格 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredContributions" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="投稿ID" width="140" />
        <el-table-column label="时间" width="140">
          <template #default="{ row }">
            {{ formatTime(row.submittedAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="contributorName" label="投稿人" width="100" />
        <el-table-column label="类型" width="80">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded',
                row.type === 'case' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'
              ]"
            >
              {{ row.type === 'case' ? '案例' : '建议' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                row.status === 'approved' ? 'bg-emerald-50 text-emerald-600' :
                'bg-red-50 text-red-600'
              ]"
            >
              {{ statusLabel(row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button
                v-if="row.status === 'pending' && hasPerm(PERMISSIONS.REVIEW_CONTRIBUTION)"
                @click="handleApprove(row)"
                class="text-emerald-500 hover:text-emerald-600 text-sm font-medium transition-colors"
              >
                通过
              </button>
              <button
                v-if="row.status === 'pending' && hasPerm(PERMISSIONS.REVIEW_CONTRIBUTION)"
                @click="handleReject(row)"
                class="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
              >
                拒绝
              </button>
              <button
                v-if="row.status !== 'pending'"
                @click="handleView(row)"
                class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors"
              >
                查看
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="filteredContributions.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <div class="text-slate-400 text-sm">暂无投稿</div>
      </div>
    </div>

    <!-- 审核对话框 -->
    <el-dialog :visible.sync="dialogVisible" title="投稿审核" width="560px">
      <div v-if="currentContribution" class="space-y-4">
        <!-- 元信息卡片 -->
        <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-4 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
          <div class="flex items-center gap-4 text-sm text-slate-500 mb-3">
            <span><strong class="text-slate-700">投稿人:</strong> {{ currentContribution.contributorName }}</span>
            <span><strong class="text-slate-700">类型:</strong> {{ currentContribution.type === 'case' ? '案例' : '建议' }}</span>
            <span><strong class="text-slate-700">时间:</strong> {{ formatTime(currentContribution.submittedAt) }}</span>
          </div>
          <div class="text-base font-bold text-slate-900">{{ currentContribution.title }}</div>
        </div>

        <!-- 内容卡片 -->
        <div class="bg-slate-50 rounded-lg p-4 space-y-3">
          <div v-if="currentContribution.scenario">
            <div class="text-xs font-semibold text-slate-400 mb-1">场景描述</div>
            <div class="text-sm text-slate-700 leading-relaxed">{{ currentContribution.scenario }}</div>
          </div>
          <div v-if="currentContribution.action">
            <div class="text-xs font-semibold text-slate-400 mb-1">核心动作</div>
            <div class="text-sm text-slate-700 leading-relaxed">{{ currentContribution.action }}</div>
          </div>
          <div v-if="currentContribution.content">
            <div class="text-xs font-semibold text-slate-400 mb-1">内容</div>
            <div class="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{{ currentContribution.content }}</div>
          </div>
        </div>

        <!-- 审核意见 -->
        <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-4 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
          <div class="flex items-center gap-2 mb-3">
            <svg class="w-4 h-4 text-[#F2C94C]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span class="text-sm font-bold text-slate-900">审核意见</span>
          </div>
          <el-form size="small">
            <el-form-item>
              <el-input
                v-model="reviewComment"
                type="textarea"
                :rows="2"
                placeholder="填写审核意见（可选）"
              />
            </el-form-item>
          </el-form>
        </div>
      </div>

      <span slot="footer">
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors mr-2"
        >
          取消
        </button>
        <button
          v-if="dialogAction === 'approve'"
          @click="confirmApprove"
          class="px-5 py-2 bg-emerald-500 text-white text-sm font-medium rounded-full hover:bg-emerald-600 transition-colors shadow-sm"
        >
          确认通过
        </button>
        <button
          v-if="dialogAction === 'reject'"
          @click="confirmReject"
          class="px-5 py-2 bg-red-500 text-white text-sm font-medium rounded-full hover:bg-red-600 transition-colors shadow-sm"
        >
          确认拒绝
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { PERMISSIONS } from '../../data/adminConfig'

export default {
  name: 'ContributionManager',

  data() {
    return {
      statusFilter: '',
      dialogVisible: false,
      dialogAction: '',
      currentContribution: null,
      reviewComment: '',
      filterTabs: [
        { label: '全部', value: '' },
        { label: '待审核', value: 'pending' },
        { label: '已通过', value: 'approved' },
        { label: '已拒绝', value: 'rejected' },
      ],
    }
  },

  computed: {
    ...mapState(['contributions']),
    ...mapGetters(['hasPermission']),

    PERMISSIONS() {
      return PERMISSIONS
    },

    filteredContributions() {
      if (!this.statusFilter) return this.contributions
      return this.contributions.filter(c => c.status === this.statusFilter)
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },
    statusType(status) {
      const map = { pending: 'warning', approved: 'success', rejected: 'danger' }
      return map[status] || 'info'
    },

    statusLabel(status) {
      const map = { pending: '待审核', approved: '已通过', rejected: '已拒绝' }
      return map[status] || status
    },

    formatTime(ts) {
      if (!ts) return '-'
      const d = new Date(ts)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },

    handleApprove(row) {
      this.currentContribution = row
      this.dialogAction = 'approve'
      this.reviewComment = ''
      this.dialogVisible = true
    },

    handleReject(row) {
      this.currentContribution = row
      this.dialogAction = 'reject'
      this.reviewComment = ''
      this.dialogVisible = true
    },

    handleView(row) {
      this.currentContribution = row
      this.dialogAction = 'view'
      this.reviewComment = row.reviewComment || ''
      this.dialogVisible = true
    },

    confirmApprove() {
      this.updateStatus(this.currentContribution.id, 'approved', this.reviewComment)
      this.dialogVisible = false
      this.$message.success('已通过')
    },

    confirmReject() {
      this.updateStatus(this.currentContribution.id, 'rejected', this.reviewComment)
      this.dialogVisible = false
      this.$message.success('已拒绝')
    },

    updateStatus(id, status, comment) {
      const updated = this.contributions.map(c => {
        if (c.id === id) {
          return {
            ...c,
            status,
            reviewComment: comment,
            reviewedAt: Date.now(),
            reviewedBy: this.$store.state.userRole,
          }
        }
        return c
      })
      this.$store.commit('SET_CONTRIBUTIONS', updated)

      logAction({
        action: 'UPDATE',
        targetType: 'contribution',
        targetId: id,
        reason: `投稿审核: ${status === 'approved' ? '通过' : '拒绝'} - ${comment || '无意见'}`,
        source: 'admin-ui',
        actor: { role: this.$store.state.userRole },
      })
    },
  },
}
</script>
